import React, {useEffect, useState} from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import {
    Button, Checkbox, CircularProgress, Dialog,
    Divider,
    FormControl, FormControlLabel, FormGroup,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Typography,
    withStyles
} from "@material-ui/core";
import WifiForm from "../../sc-settings/Presets/WifiForm";
import {Delete} from "@material-ui/icons";
import {snackActions} from "../../sc-utilities/SnackBar";
import {parseURL} from "../../sc-utilities";
import AppLogoLoading from "../../sc-logo";

const styles = theme => ({
    root: {
        width: "auto",
        height: "auto",
        padding: theme.spacing(2),
    },
    form: {
        width: '95%',
        margin: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    submit: {
        width: "100%",
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2),
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.buttonText.default
        }
    },
    addButton: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2),
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.buttonText.default
        }
    },
    text: {
        borderRadius: '1px',
        marginRight: 10,
        color: theme.palette.buttonText.default
    },
    divider: {
        margin: theme.spacing(1),

    },
    wifiDivider: {
        margin: "15px 0px",
    },
    formControl: {
        width: "100%"
    },
    row: {
        display: 'row',
        width: "auto",
        alignItems: 'center'
    },
    loadingScreen: {
        backgroundColor: "transparent",
        boxShadow: "none",
    },
});

const securazeInitialValues = {
    PresetsEnabled: false,
    OrderID: null,
    TransportColliID: null,
    ColliID: null,
    GradeID: null,
    Wifis: []
}

const RaspberryEraseStationPresets = (props) => {
    const { classes, translations, activeCustomerId, handleSubmit, grades, orders, collies, updatePresets, loading, setLoading, loadRaspberryEraseStation } = props;
    const [raspberryEraseStation, setRaspberryEraseStation] = useState(null);
    const presets = raspberryEraseStation?.Config? JSON.parse(raspberryEraseStation.Config) : null;

    const searchParams = parseURL(window.location.href);

    useEffect(() => {
        const getRaspberryEraseStation = async () => {
            setLoading(true);
            const result = await loadRaspberryEraseStation({
                RaspberryEraseStationID: searchParams?.EraseStationID,
                ActiveCustomerID: activeCustomerId
            });
            setLoading(false);
            if (result) {
                setRaspberryEraseStation(result);
            }
        }

        getRaspberryEraseStation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const initialValues = {
        PresetsEnabled: presets ? !!presets?.PresetsEnabled : securazeInitialValues.PresetsEnabled,
        OrderID: presets?.OrderID || null,
        TransportColliID: presets?.TransportColliID || null,
        ColliID: presets?.ColliID || null,
        GradeID: presets?.GradeID || null,
        Wifis: presets ? presets.Wifis?? [] : []
    };

    const wifiObject = Yup.object().shape({
        Security: Yup.string().required(),
        SSID: Yup.string().required(),
        Password: Yup.string().required(),
        Username: Yup.string().when("Security", {
            is: (value) => ["wpa_enterprise", "wpa2_enterprise", "wpa3_enterprise"].includes(value),
            then: Yup.string().required()
        })
    });

    const formSettings = {
        Validation: {
            Wifis: Yup.array().of(wifiObject)
        },
        ButtonSubmit: {
            Title: translations["save"] || "SAVE",
            Color: 'primary'
        }
    };

    const loadingScreen =   <Dialog disableBackdropClick={true} disableEscapeKeyDown={false} open={loading} PaperProps={{classes: {root: classes.loadingScreen}}}>
                                <AppLogoLoading/>
                            </Dialog>

    return (
        <Paper className={classes.root}>
            {raspberryEraseStation?
                <Formik
                    initialValues={initialValues}
                    onSubmit={async (values, { setSubmitting }) => {
                        setSubmitting(true);
                        await handleSubmit(values);
                        setSubmitting(false);
                    }}
                    enableReinitialize
                    validationSchema={Yup.object().shape(formSettings.Validation)}
                >
                    {formikProps => {
                        const { values, isSubmitting, setFieldValue, handleChange, isValid, setValues, handleSubmit } = formikProps;
                        const gradeID = (grades ? grades : []).filter(grade => grade.value === values.GradeID).length > 0;
                        const orderID = (orders ? orders : []).filter(order => order?.OrderID === values.OrderID).length > 0;
                        const transportColliID = (orders ? orders : []).filter(el => el.OrderID === values.OrderID ? el.OrderID : []).filter(colli => colli?.TransportCollies.filter(el => el.TransportColliID === values.TransportColliID)).length > 0;
                        const colliID = (collies ? collies : []).filter(colli => colli?.value === values.ColliID).length > 0;

                        const addWifi = () => {
                            setValues({...values, Wifis: [...values.Wifis, {Security: "", SSID: "", Username: "", Password: ""}]})
                        }

                        const removeWifi = (index) => {
                            setValues({...values, Wifis: values.Wifis.filter((value, i) => i !== index)});
                        }

                        return (
                            <form className={classes.form} onSubmit={e => {
                                e.preventDefault();
                                handleSubmit(e);
                            }}>
                                <Button className={classes.text} variant="contained" disabled={!values.PresetsEnabled} color={'primary'} onClick={async () => {
                                    let serverResult;
                                    setLoading(true);
                                    serverResult = await updatePresets({ Config: securazeInitialValues, RaspberryEraseStationID: raspberryEraseStation.EraseStationID, ActiveCustomerID: activeCustomerId });
                                    setLoading(false);
                                    if (serverResult) {
                                        setValues({...securazeInitialValues})
                                    }
                                    if (serverResult?.status) {
                                        return snackActions.snackBarComponent(translations['config_updated'] || "Config updated", "success");
                                    } else {
                                        return snackActions.snackBarComponent(translations['failed_to_update_config'] || "Error: Failed to update config", "error");
                                    }
                                }}>
                                    {translations['reset'] || 'Reset'}
                                </Button>
                                <Typography align='center' variant={"h5"}>{translations['preset_basic'] || 'Preset basic'}</Typography>
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                color={'primary'}
                                                margin={"dense"}
                                                name={"PresetsEnabled"}
                                                checked={!!values.PresetsEnabled || false}
                                                onChange={handleChange}
                                            />
                                        }
                                        label={`${translations['enable_preset'] || 'Enable preset'}`}
                                    />
                                </FormGroup>
                                <FormControl className={classes.formControl} margin={"dense"}>
                                    <InputLabel shrink>{translations["order_select"] || "Please select order"}</InputLabel>
                                    <Select
                                        name="OrderID"
                                        label={translations["order"] || 'Order'}
                                        margin={"dense"}
                                        value={orderID ? values.OrderID : ''}
                                        onChange={(event) => { setFieldValue('TransportColliID', ''); handleChange(event); return event.stopPropagation() }}
                                        disabled={!values.PresetsEnabled}
                                    >
                                        <MenuItem value="">
                                            <em>{translations["none"] || "None"}</em>
                                        </MenuItem>
                                        {(orders ? orders : []).map(option => (
                                            <MenuItem key={option.OrderID} value={option.OrderID}>
                                                {option.Name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl className={classes.formControl} margin={"dense"}>
                                    <InputLabel shrink>{translations["transport_colli_text"] || "Please select transport colli"}</InputLabel>
                                    <Select
                                        name="TransportColliID"
                                        label={translations["transport_colli"] || 'Transport Colli'}
                                        margin={"dense"}
                                        value={transportColliID ? values.TransportColliID + '' : ''}
                                        onChange={handleChange}
                                        disabled={!values.PresetsEnabled}
                                    >
                                        <MenuItem value="">
                                            <em>{translations["none"] || "None"}</em>
                                        </MenuItem>
                                        {(orders?.length > 0 ? orders.filter(order => order.OrderID === values.OrderID) : []).map(
                                            order => {
                                                return order.TransportCollies.map(option => (
                                                    <MenuItem key={option.TransportColliID} value={option.TransportColliID}>
                                                        {option.Name}
                                                    </MenuItem>

                                                ))
                                            }
                                        )}
                                    </Select>
                                </FormControl>
                                <FormControl className={classes.formControl} margin={"dense"}>
                                    <InputLabel shrink>{translations["colli_text"] || "Please select container"}</InputLabel>
                                    <Select
                                        name="ColliID"
                                        label={translations["colli"] || 'Colli'}
                                        margin={"dense"}
                                        value={colliID ? values.ColliID : ''}
                                        onChange={handleChange}
                                        disabled={!values.PresetsEnabled}
                                    >
                                        <MenuItem value="">
                                            <em>{translations["none"] || "None"}</em>
                                        </MenuItem>
                                        {(collies ? collies : []).map(option => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.displayValue}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Divider className={classes.divider} variant="middle" />
                                <Typography align='center' variant={"h5"}>{translations['preset_advanced'] || 'Presets advanced'}</Typography>
                                <FormControl className={classes.formControl} margin={"dense"}>
                                    <InputLabel shrink>{translations["grade_text"] || "Please select grade"}</InputLabel>
                                    <Select
                                        name="GradeID"
                                        label={translations['grade'] || 'Grade'}
                                        margin={"dense"}
                                        defaultValue={''}
                                        value={gradeID ? values.GradeID : ''}
                                        onChange={handleChange}
                                        disabled={!values.PresetsEnabled}
                                    >
                                        {(grades ? grades : []).map(option => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.displayValue}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Divider className={classes.divider} variant="middle" />
                                <br />
                                <Typography align='center' variant={"h5"}>{translations['wifis'] || 'Wifis'}</Typography>
                                {values?.Wifis?.map((wifi, index) => (
                                    <React.Fragment key={index}>
                                        <Grid container direction="row" justify="center" alignItems="center" spacing={5}>
                                            <Grid item xs={12} sm={10}>
                                                <WifiForm translations={translations} index={index} formikProps={formikProps} />
                                            </Grid>
                                            <Grid item xs={12} sm={2}>
                                                <Button
                                                    type="button"
                                                    variant="contained"
                                                    onClick={() => removeWifi(index)}
                                                    disabled={!values.PresetsEnabled}
                                                >
                                                    <Delete />
                                                </Button>
                                            </Grid>
                                        </Grid>
                                        <Divider className={classes.wifiDivider} variant="middle" />
                                    </React.Fragment>
                                ))}
                                <Button
                                    className={classes.addButton}
                                    variant="contained"
                                    color={formSettings.ButtonSubmit.Color}
                                    type="button"
                                    disabled={!isValid || !values.PresetsEnabled}
                                    onClick={addWifi}
                                >
                                    {translations['add_wifi'] || 'Add wifi'}
                                </Button>

                                <div>
                                    {formSettings.ButtonSubmit ? (
                                        <Button
                                            className={classes.submit}
                                            variant="contained"
                                            color={formSettings.ButtonSubmit.Color}
                                            type="submit"
                                            disabled={!isValid || isSubmitting || loading}
                                        >
                                            { isSubmitting ? <CircularProgress size={24} /> : formSettings.ButtonSubmit.Title}
                                        </Button>
                                    ) : null}
                                </div>
                            </form>
                        );
                    }}
                </Formik> : loadingScreen }
        </Paper>
    )
}

export default (withStyles(styles, { withTheme: true })(RaspberryEraseStationPresets));